import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { Radio, RadioGroup, FormControlLabel } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import RoutePrompt from 'components/shared/RoutePrompt';
import CircularProgress from 'components/shared/CircularProgress';
import { Input, Checkbox } from 'components/FormInputs';
import AcceptTerms from 'components/shared/AcceptTerms';
import {
  validateNaturalHostInformation,
  validateLegalHostInformation,
  cleanHostInformationRequestData,
} from 'logic/userForm';
import { useFormik } from 'components/shared/Formik';
import useTextTransformBlur from 'hooks/useTextTransformBlur';
import { capitalizeFirst, capitalizeWords, combineName } from 'logic/textTransform';
import HostInformationFormNaturalSection from './Natural';
import HostInformationFormLegalSection from './Legal';
import ProxyConsentBlock from '../ProxyConsentBlock';

const HostInformationForm = ({
  user,
  onSubmit,
  mangoUserId,
  loading,
  setActiveStep,
  requiredIban,
  disableIban,
}) => {
  const intl = useIntl();

  // Preset the personnummer from the BankID-verified number (century dropped
  // to the 10-digit tin form) when it isn't stored yet.
  const bankidTinNumber =
    user.type_of_person === 'NATURAL' && user.bankid_personal_number
      ? user.bankid_personal_number.substring(2)
      : '';

  const formik = useFormik({
    initialValues: {
      tin_number: user.tin_number || bankidTinNumber,
      first_name: user.first_name,
      last_name: user.last_name,
      u_address1: user.u_address1 || '',
      u_address2: user.u_address2 || '',
      u_postal_code: user.u_postal_code || '',
      u_city: user.u_city || '',
      u_country: user.u_country || '',
      u_region: user.u_region || '',
      iban: user?.iban || '',
      owner:
        user.type_of_person === 'NATURAL'
          ? combineName(user.first_name, user.last_name)
          : user.business_name,
      hq_address1: user.type_of_person === 'NATURAL' ? '' : user.hq_address1 || '',
      hq_address2: user.type_of_person === 'NATURAL' ? '' : user.hq_address2 || '',
      hq_postal_code: user.type_of_person === 'NATURAL' ? '' : user.hq_postal_code || '',
      hq_city: user.type_of_person === 'NATURAL' ? '' : user.hq_city || '',
      hq_country: user.type_of_person === 'NATURAL' ? '' : user.hq_country || '',
      hq_region: user.type_of_person === 'NATURAL' ? '' : user.hq_region || '',
      company_number: user.type_of_person === 'NATURAL' ? '' : user.company_number || '',
      vat_registered: user.type_of_person === 'NATURAL' ? '' : user.vat_registered || '',
      vat_number: user.type_of_person === 'NATURAL' ? '' : user.vat_number || '',
      vat_rate: user.type_of_person === 'NATURAL' ? '' : String(user.vat_rate || '0'),
      birthday: user.birthday,
      nationality: user.nationality,
      country_of_residence: user.country_of_residence,
      business_name: user.type_of_person === 'NATURAL' ? '' : user.business_name || '',
      business_type: user.type_of_person === 'NATURAL' ? '' : user.business_type || '',
      termsOfPayment: !!mangoUserId,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validate:
      user.type_of_person === 'NATURAL'
        ? validateNaturalHostInformation({ intl }, false, false, !mangoUserId)
        : validateLegalHostInformation({ intl }, false, !mangoUserId),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await onSubmit(cleanHostInformationRequestData(values));
      } catch (err) {
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    values,
    handleSubmit,
    handleChange,
    isSubmitting,
    pristine,
    dirty,
    setFieldValue,
  } = formik;

  const handleBlur = useTextTransformBlur(
    formik,
    {
      first_name: capitalizeFirst,
      last_name: capitalizeFirst,
      business_name: capitalizeFirst,
      u_address1: capitalizeWords,
      u_address2: capitalizeWords,
      u_city: capitalizeWords,
      hq_address1: capitalizeWords,
      hq_address2: capitalizeWords,
      hq_city: capitalizeWords,
    },
    {
      onAfterTransform: (fieldName, effectiveValue) => {
        if (values.business_type !== 'SOLETRADER') return;
        if (fieldName !== 'first_name' && fieldName !== 'last_name') return;

        const firstRaw = fieldName === 'first_name' ? effectiveValue : values.first_name;
        const lastRaw = fieldName === 'last_name' ? effectiveValue : values.last_name;

        const first = capitalizeFirst(String(firstRaw ?? '').trim());
        const last = capitalizeFirst(String(lastRaw ?? '').trim());

        if (first && last) {
          const combined = combineName(first, last);
          setFieldValue('first_name', first);
          setFieldValue('last_name', last);
          setFieldValue('business_name', combined);
          setFieldValue('owner', combined);
        }
      },
    },
  );

  const formikWithBlur = { ...formik, handleBlur };

  const completed = user.dac7_valid;

  return (
    <Box>
      {requiredIban && (
        <Box mb={2}>
          <ProxyConsentBlock />
        </Box>
      )}
      <form onSubmit={handleSubmit} noValidate>
        <RoutePrompt when={(!pristine && dirty) || isSubmitting} />

        <Box flex={1} p={2} bgcolor="white">
          <Box mb={2}>
            <Typography variant="h6">
              <FormattedMessage id="payout.steps.host" defaultMessage="Host information" />
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2">
                <FormattedMessage
                  id="user.type"
                  defaultMessage="Do you rent out as a private person or as a company?"
                />
              </Typography>
              <RadioGroup
                row
                name="type_of_person"
                value={user.type_of_person}
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                disabled
              >
                <FormControlLabel
                  disabled
                  value="NATURAL"
                  control={<Radio color="primary" />}
                  label={intl.formatMessage({
                    id: 'Private person',
                    defaultMessage: 'Private person',
                  })}
                />
                <FormControlLabel
                  disabled
                  value="LEGAL"
                  control={<Radio color="primary" />}
                  label={intl.formatMessage({ id: 'Company', defaultMessage: 'Company' })}
                />
              </RadioGroup>
            </Grid>
            {user.type_of_person === 'NATURAL' && (
              <HostInformationFormNaturalSection formik={formikWithBlur} user={user} />
            )}
            {user.type_of_person === 'LEGAL' && (
              <HostInformationFormLegalSection formik={formikWithBlur} user={user} />
            )}
            {requiredIban && (
              <>
                <Box mt={2}>&nbsp;</Box>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    <FormattedMessage
                      id="bankaccounts.step.bankaccount"
                      defaultMessage="Bank account"
                    />
                  </Typography>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Input
                    name="owner"
                    value={
                      user.type_of_person === 'NATURAL'
                        ? combineName(values.first_name, values.last_name)
                        : values.business_name
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                    label={<FormattedMessage id="Owner" defaultMessage="Owner" />}
                    help={
                      user.type_of_person === 'NATURAL' ? (
                        <FormattedMessage
                          id="Owner.help.privateperson"
                          defaultMessage="First name and last name"
                        />
                      ) : (
                        <FormattedMessage id="Owner.help.company" defaultMessage="Company name" />
                      )
                    }
                    disabled
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Input
                    name="iban"
                    value={values.iban}
                    onChange={(e) => {
                      e.target.value = (e.target.value || '').replace(/-/g, '').replace(/\s/g, '');

                      return handleChange(e);
                    }}
                    onBlur={handleBlur}
                    errors={errors}
                    label={<FormattedMessage id="IBAN" defaultMessage="IBAN" />}
                    disabled={disableIban}
                    help={
                      disableIban ? (
                        <FormattedMessage
                          id="payout.iban.adminDisabled"
                          defaultMessage="IBAN can't be changed when editing as an admin"
                        />
                      ) : undefined
                    }
                  />
                </Grid>
              </>
            )}

            {!mangoUserId && (
              <Grid item xs={12}>
                <Checkbox
                  name="termsOfPayment"
                  value={values.termsOfPayment}
                  onChange={handleChange}
                  label={<AcceptTerms />}
                  errors={errors}
                />
              </Grid>
            )}
          </Grid>
          <Box py={2}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              disabled={pristine || !dirty || isSubmitting || loading}
            >
              {loading && <CircularProgress size={20} />}
              {!loading && <FormattedMessage id="Save" defaultMessage="Save" />}
            </Button>
          </Box>
          {completed && !!setActiveStep && (
            <Box textAlign="right">
              <Button
                size="small"
                variant="contained"
                color="primary"
                type="button"
                onClick={() => setActiveStep(1)}
              >
                <FormattedMessage id="ID-documents" defaultMessage="ID-documents →" />
              </Button>
            </Box>
          )}
        </Box>
      </form>
    </Box>
  );
};

HostInformationForm.propTypes = {
  mangoUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  user: PropTypes.shape({
    dac7_valid: PropTypes.bool,
    type_of_person: PropTypes.string,
    bankid_personal_number: PropTypes.string,
    business_type: PropTypes.string,
    business_name: PropTypes.string,
    company_number: PropTypes.string,
    tin_number: PropTypes.string,
    birthday: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    iban: PropTypes.string,
    country_of_residence: PropTypes.string,
    nationality: PropTypes.string,
    vat_registered: PropTypes.string,
    vat_number: PropTypes.string,
    vat_rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    u_address1: PropTypes.string,
    u_address2: PropTypes.string,
    u_city: PropTypes.string,
    u_postal_code: PropTypes.string,
    u_country: PropTypes.string,
    u_region: PropTypes.string,
    hq_address1: PropTypes.string,
    hq_address2: PropTypes.string,
    hq_postal_code: PropTypes.string,
    hq_city: PropTypes.string,
    hq_country: PropTypes.string,
    hq_region: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  setActiveStep: PropTypes.func,
  requiredIban: PropTypes.bool,
  disableIban: PropTypes.bool,
};

export default HostInformationForm;
